import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Popup from 'src/common/refactoredComponents/popup/Popup';
import Button from 'src/common/refactoredComponents/button/Button';
import Select from 'src/common/refactoredComponents/select/Select';
import { ButtonStyles } from 'src/constants/buttonStyles';
import {
    Container,
    FieldContainer,
    FieldLabel,
    WarningContainer,
    WarningText,
    ButtonContainer,
    SelectedTerritoryValue,
    SelectedPlaceholder,
} from './AssignTerritoryPopup.styled';
import Error from '@material-ui/icons/Error';
import { BASE_TEXT_PX, FONT_FAMILY_DEMI , AWAITING_FONT_COLOR} from 'src/styles/fonts/MCFontConstants.js';
import Spinner from 'src/common/refactoredComponents/spinner/Spinner';
import { sizes } from 'src/styles/Media.styled';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import { getTerritoryCredit, resetSaveTerritoryCredit, saveTerritoryCredit } from '../../redux/actions';

const AssignTerritoryPopup = ({
    orderNumber,
    onClose,
    onConfirm,
    showWarning = true,
    warningMessage = '',
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [selectedTerritories, setSelectedTerritories] = useState({});
    const loading = useSelector((state) => state?.territoryCreditReducer?.loading);
    const territoryCreditData = useSelector((state) => state?.territoryCreditReducer?.territoryCreditData);
    const saveTerritoryCreditData = useSelector((state) => state?.territoryCreditReducer?.saveTerritoryCreditData);
    const { isLanguageChanged } = useSelector((state) => state.loginReducer);

    const getTerritoryCodeFromKey = (key = '') => {
        const match = String(key).match(/\b[A-Z]{2}\d{3}\b/i);
        return match ? match[0].toUpperCase() : '';
    };

    const getSelectedValueFromTerritoryField = (field, territoryCode) => {
        const territoryFieldValue = territoryCreditData?.[field];
        if (!territoryFieldValue || typeof territoryFieldValue !== 'string') {
            return undefined;
        }

        const [code, ...valueParts] = territoryFieldValue.split('_');
        if (!valueParts.length) {
            return undefined;
        }

        if (territoryCode && code && territoryCode !== code) {
            return undefined;
        }

        const value = valueParts.join('_');
        if (value === undefined || value === null || value === '') {
            return undefined;
        }

        return /^\d+$/.test(value) ? Number(value) : value;
    };

    useEffectWithErrorLogging(() => {
        // intialize intial selected value
        if (territoryCreditData?.statusCode == '10001' && territoryCreditData?.salesRepMap?.length > 0) {
            const initialSelectedTerritories = {};

            territoryCreditData?.salesRepMap?.forEach((item, index) => {
                const field = getFieldName(item.key);
                if (field) {
                    const territoryCode = getTerritoryCodeFromKey(item.key);
                    const parsed = getSelectedValueFromTerritoryField(field, territoryCode);

                    if (parsed === undefined) {
                        return;
                    }

                    initialSelectedTerritories[field] = parsed;
                    
                    let data = territoryCreditData?.salesRepMap?.[index]?.value;
                    if (data) {
                        const parsedData = parseResponse(data);            
                        let i = parsedData.findIndex(item => item.value === parsed);
                        if(i >= 0){
                            let territoryName = parsedData[i].label;
                            initialSelectedTerritories[`${field}Name`] = territoryName;
                        }
                    }
                }
            });

            setSelectedTerritories(initialSelectedTerritories);
        }
    }, [territoryCreditData]);

    const getFieldName = (key) => {
        // US001 and US026 are salesRepNonInter
        // All others (US006, Latin America, EMEA, Asia-Pacific) are salesRepInter
        if (key.includes('US001') || key.includes('US026')) {
            return 'salesRepNonInter';
        }
        return 'salesRepInter';
    };

    const parseResponse = (input) => {
        return input.map(s =>
            Object.fromEntries(
                s.slice(1, -1).split(', ').map(p => {
                    const [k, ...v] = p.split('=');
                    let value = v.join('=');

                    if (value === 'true' || value === 'false') value = value === 'true';
                    else if (/^\d+$/.test(value) && k !== 'address') value = Number(value);

                    return [k, value];
                })
            )
        );
    };

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm(selectedTerritories);
        }
        onClose();
    };

    const handleTerritoryChange = (value, key, index) => {
        const selectedTerritoriesCopy = { ...selectedTerritories };
        const field = getFieldName(key);
        selectedTerritoriesCopy[field] = value;
        let data = territoryCreditData?.salesRepMap?.[index]?.value;
        if (data) {
            const parsedData = parseResponse(data);            
            let i = parsedData.findIndex(item => item.value === value);
            if(i >= 0){
                let territoryName = parsedData[i].label;
                selectedTerritoriesCopy[`${field}Name`] = territoryName;
            }
        }
        setSelectedTerritories(selectedTerritoriesCopy);
    };

    useEffectWithErrorLogging(() => {
        dispatch(getTerritoryCredit(orderNumber));
    }, []);

    useEffectWithErrorLogging(() => {
        if (saveTerritoryCreditData?.statusCode == '10802') {
            handleConfirm();
            dispatch(resetSaveTerritoryCredit());
        }
    }, [saveTerritoryCreditData]);

    const save = () => {
        const payload = {
            sstRef: orderNumber,
        };

        territoryCreditData?.salesRepMap?.forEach((item) => {
            const field = getFieldName(item.key);
            const selectedValue = selectedTerritories[field];
            const territoryCode = getTerritoryCodeFromKey(item.key);

            if (
                field &&
                territoryCode &&
                selectedValue !== undefined &&
                selectedValue !== null &&
                selectedValue !== ''
            ) {
                const formattedSelectedValue = String(selectedValue).padStart(3, '0');
                payload[field] = `${territoryCode}_${formattedSelectedValue}`;
            }
        });
        
        dispatch(saveTerritoryCredit(payload));
    };

    const isNarrowViewport = window.innerWidth < 400;
    const popupContentPadding = isNarrowViewport
        ? '10px 10px 10px 10px'
        : window.innerWidth <= sizes.mobileDevice
            ? '10px 15px 0px 15px'
            : '15px';

    return (
        <Popup
            id="orders-assignterritory-popup"
            title={isLanguageChanged 
                ? t('orders.assignTerritory.assignTerritoryTitle', { orderNumber: orderNumber || '', interpolation: { escapeValue: false }})
                : `${t('orders.assignTerritory.title')}${orderNumber || ''}`}
            onClose={onClose}
            width="90%"
            webWidth="410px"            
            borderRadius="6px"
            popupContentPadding={popupContentPadding}
            mobileWidthPercent={'98%'}
            DeskFontSize="18px"
        >
            <Container>
                {loading ?
                    <Spinner
                        id="orders-assignTerritoryPopup-loadingSpinner"
                        animation="border"
                        variant="primary"
                        small
                    />
                    :
                    <>
                        {territoryCreditData?.salesRepMap?.map((item, index) => {
                            const dropdownArray = parseResponse(item.value);
                            const field = getFieldName(item.key);
                            const selectedValue = selectedTerritories[field];
                            return (
                                <FieldContainer key={index}>
                                    <FieldLabel>{item.key}</FieldLabel>
                                    <Select
                                        id="orders-assignterritory-select"
                                        value={selectedValue}
                                        options={dropdownArray}
                                        onChange={(value) => handleTerritoryChange(value, item.key, index)}
                                        isSortMobileView
                                        placeholder={t('orders.assignTerritory.selectPlaceholder')}
                                        optionLabel="label"
                                        optionValue="value"
                                        width="100%"
                                        height="40px"
                                        borderRadius="6px"
                                        disabled={dropdownArray.length === 0}
                                        valueTemplate={(option, props) => {
                                            if (option) {
                                                return (
                                                    <SelectedTerritoryValue width= {
                                                        window.innerWidth <= sizes.mobileDevice
                                                        ? '100%'
                                                        : '99%'
                                                    }>
                                                        {option.label}
                                                    </SelectedTerritoryValue>
                                                );
                                            }
                                            return (
                                                <SelectedPlaceholder>
                                                    {props.placeholder}
                                                </SelectedPlaceholder>
                                            );
                                        }}
                                    />
                                </FieldContainer>
                            );
                        })}

                        {showWarning && (
                            <WarningContainer>
                                <Error style={{ color: AWAITING_FONT_COLOR }} />
                                <WarningText>
                                    {warningMessage || t('orders.assignTerritory.warningMessage')}
                                </WarningText>
                            </WarningContainer>
                        )}

                        <ButtonContainer>
                            <Button
                                id="orders-assignterritory-cancel-button"
                                show
                                variant={ButtonStyles?.secondary}
                                width="110px"
                                height="40px"
                                fontSize={BASE_TEXT_PX}
                                borderRadius="1600px"
                                fontFamily={FONT_FAMILY_DEMI}
                                clicked={onClose}
                            >
                                {t('common.cancelButton')}
                            </Button>
                            <Button
                                id="orders-assignterritory-ok-button"
                                show
                                variant={!selectedTerritories.salesRepNonInter && !selectedTerritories.salesRepInter ? ButtonStyles?.disabled : ButtonStyles?.primary}
                                width="80px"
                                minWidth="80px"
                                height="40px"
                                fontSize={BASE_TEXT_PX}
                                borderRadius="1600px"
                                fontFamily={FONT_FAMILY_DEMI}
                                clicked={save}
                                disabled={!selectedTerritories.salesRepNonInter && !selectedTerritories.salesRepInter}
                            >
                                {t('common.okUpperCaseButton')}
                            </Button>
                        </ButtonContainer>
                    </>
                }
            </Container>
        </Popup>
    );
};

export default AssignTerritoryPopup;
